import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, X, Trophy, Medal } from 'lucide-react';
import { conquistasAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function AdminConquistas() {
  const [conquistas, setConquistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConquista, setEditingConquista] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data: '',
    posicao: '',
    imagem_url: ''
  });
  const { toast } = useToast();

  const fetchConquistas = async () => {
    try {
      const res = await conquistasAPI.listar();
      setConquistas(res.data);
    } catch (error) {
      console.error('Erro ao carregar conquistas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConquistas();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConquista) {
        await conquistasAPI.atualizar(editingConquista.id, formData);
        toast({ title: "Conquista atualizada com sucesso!" });
      } else {
        await conquistasAPI.criar(formData);
        toast({ title: "Conquista adicionada com sucesso!" });
      }
      
      setModalOpen(false);
      setEditingConquista(null);
      setFormData({ titulo: '', descricao: '', data: '', posicao: '', imagem_url: '' });
      fetchConquistas();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleEdit = (conquista) => {
    setEditingConquista(conquista);
    setFormData({
      titulo: conquista.titulo,
      descricao: conquista.descricao || '',
      data: conquista.data,
      posicao: conquista.posicao || '',
      imagem_url: conquista.imagem_url || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conquista?')) return;
    try {
      await conquistasAPI.deletar(id);
      toast({ title: "Conquista excluída com sucesso!" });
      fetchConquistas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="admin-conquistas">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Conquistas</h1>
          <p className="text-slate-600 mt-1">Gerencie os títulos e conquistas do time</p>
        </div>
        <button
          onClick={() => {
            setEditingConquista(null);
            setFormData({ titulo: '', descricao: '', data: '', posicao: '', imagem_url: '' });
            setModalOpen(true);
          }}
          data-testid="add-conquista-btn"
          className="bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-800 transition-colors"
        >
          <Plus size={20} />
          Adicionar Conquista
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : conquistas.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Trophy className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhuma conquista cadastrada</h3>
          <p className="text-slate-500">Clique em "Adicionar Conquista" para começar.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {conquistas.map((conquista, index) => (
            <motion.div
              key={conquista.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-6 shadow-sm"
              data-testid={`conquista-card-${conquista.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Medal className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(conquista)}
                    data-testid={`edit-conquista-${conquista.id}`}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(conquista.id)}
                    data-testid={`delete-conquista-${conquista.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                {conquista.titulo}
              </h3>
              
              {conquista.descricao && (
                <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                  {conquista.descricao}
                </p>
              )}

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{conquista.data}</span>
                {conquista.posicao && (
                  <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-bold">
                    {conquista.posicao}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="conquista-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-lg"
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold">
                {editingConquista ? 'Editar Conquista' : 'Nova Conquista'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Título *</label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  required
                  data-testid="modal-input-titulo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome da conquista"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Data *</label>
                  <input
                    type="text"
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    required
                    data-testid="modal-input-data"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                    placeholder="Ex: 17 de Maio de 2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Posição</label>
                  <input
                    type="text"
                    name="posicao"
                    value={formData.posicao}
                    onChange={handleChange}
                    data-testid="modal-input-posicao"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                    placeholder="Ex: 1º Lugar"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={3}
                  data-testid="modal-input-descricao"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                  placeholder="Detalhes da conquista..."
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">URL da Imagem</label>
                <input
                  type="text"
                  name="imagem_url"
                  value={formData.imagem_url}
                  onChange={handleChange}
                  data-testid="modal-input-imagem"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="https://..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="modal-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800"
                >
                  {editingConquista ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
